/*
  AL Trial cart merge — duplicate 8-day trial consolidation (Trial brief §12 "Option 2").

  Problem: Seal Subscriptions works per line item. Two 8-day trial lines in one
  order become two separate 30-day subscriptions on day 8. Ilari (2026-09-10)
  asked for the cart to swap 2 × 8-day into 1 × 16-day, which Seal already
  transitions into a single 30-day subscription on day 16.

  Contract (fail-closed):
    - Nothing runs unless window.AL_TRIAL_CART_MERGE.enabled is true AND at
      least one rule has both a from (8-day) and a to (16-day) variant id.
    - Rules are keyed per flavour. Only lines whose variant_id equals a rule's
      `from` are ever touched. Everything else in the cart is left alone.
    - A subscription 8-day line is merged only when the rule carries the
      16-day selling plan id (`planTo`). If `planFrom` is set, the line's plan
      must match it. Missing / mismatching plan => skip, never guess.
    - One-time (no selling plan) lines are merged only when mergeOneTime is on.
    - Mixed flavours (Original 8-day + Decaf 8-day) are NOT merged — they are
      different rules with quantity 1 each. That case needs a product decision.
    - qty 3 => 1 × 16-day + 1 × 8-day (pairs fold, remainder stays).
    - Lines with final_line_price 0 (gift rows) are ignored.

  Execution order (measured on DEV 2026-09-10): the 16-day line is added FIRST,
  then the cart is re-read and the 8-day lines are reduced by variant + plan
  with FRESH keys. Shopify re-keys subscription lines after another add, so a
  key captured before the add returns 400 「パラメータが無効です」. If reducing
  still fails after retries, the added 16-day quantity is rolled back and the
  merger halts for the session so it can never re-add.

  The planner is pure and exported for node tests (briefs/trial-offer-lp/cart-merge.test.cjs).
*/
(function () {
  'use strict';

  var TAG = '[al-trial-cart-merge]';
  var BUNDLER_PROPS = { _bndlr: true, __: true }; // Bundler app internals — never copy onto the 16-day line

  function toId(value) {
    var n = parseInt(String(value == null ? '' : value).replace(/[^0-9]/g, ''), 10);
    return isNaN(n) || n <= 0 ? 0 : n;
  }

  function normaliseConfig(raw) {
    raw = raw || {};
    var rules = (raw.rules || []).map(function (rule) {
      return {
        key: String(rule.key || ''),
        from: toId(rule.from),
        to: toId(rule.to),
        planFrom: toId(rule.planFrom),
        planTo: toId(rule.planTo)
      };
    }).filter(function (rule) { return rule.from > 0 && rule.to > 0 && rule.from !== rule.to; });
    return {
      enabled: Boolean(raw.enabled),
      mergeOneTime: Boolean(raw.mergeOneTime),
      debug: Boolean(raw.debug),
      rules: rules
    };
  }

  // /cart.js exposes the plan as selling_plan_allocation.selling_plan.id (not selling_plan_id).
  function linePlanId(item) {
    var alloc = item && item.selling_plan_allocation;
    if (!alloc) return 0;
    if (alloc.selling_plan && alloc.selling_plan.id) return toId(alloc.selling_plan.id);
    return toId(alloc.selling_plan_id);
  }

  function sourceLines(cart, variantId, planId, requestMarker) {
    return (cart && cart.items || []).filter(function (item) {
      return toId(item.variant_id) === variantId && item.final_line_price > 0 && linePlanId(item) === planId && String(item.properties && item.properties._trial_gift_request || '') === String(requestMarker || '');
    });
  }

  function carriedProperties(properties) {
    var out = {};
    Object.keys(properties || {}).forEach(function (key) { if (!BUNDLER_PROPS[key]) out[key] = properties[key]; });
    return Object.keys(out).length ? out : null;
  }

  // Once the 16-day trial is present, do not offer the same flavour's 8-day
  // trial again. Exact configured variant IDs keep unrelated upsells and mixed
  // flavours untouched.
  function hiddenUpsellVariantIds(cart, rawConfig) {
    var config = normaliseConfig(rawConfig);
    if (!config.enabled || !config.rules.length || !cart || !Array.isArray(cart.items)) return [];
    return config.rules.filter(function (rule) {
      return cart.items.some(function (item) {
        return toId(item.variant_id) === rule.to && item.quantity > 0 && item.final_line_price > 0;
      });
    }).map(function (rule) { return rule.from; });
  }

  // Pure planner. Returns:
  //   ops:     [{type:'add', id, quantity, selling_plan?, properties?}, {type:'change', key, quantity}...] (add first)
  //   merged:  [{rule, from, to, pairs, remainder, plan, sourcePlan, properties}] — what execute() acts on
  //   skipped: [{rule, reason, quantity}]
  function plan(cart, rawConfig) {
    var config = normaliseConfig(rawConfig);
    var result = { ops: [], skipped: [], merged: [] };
    if (!config.enabled || !config.rules.length || !cart || !Array.isArray(cart.items)) return result;

    config.rules.forEach(function (rule) {
      var groups = {};
      cart.items.forEach(function (item) {
        if (toId(item.variant_id) !== rule.from) return;
        if (!(item.final_line_price > 0)) return; // never touch ¥0 gift rows
        var planId = linePlanId(item);
        var groupKey = JSON.stringify([planId, String(item.properties && item.properties._trial_gift_request || '')]);
        (groups[groupKey] = groups[groupKey] || []).push(item);
      });

      Object.keys(groups).forEach(function (planKey) {
        var groupIdentity = JSON.parse(planKey);
        var planId = toId(groupIdentity[0]);
        var requestMarker = groupIdentity[1];
        var lines = groups[planKey];
        if (requestMarker && lines.some(function(line){return canonical(carriedProperties(line.properties)) !== canonical(carriedProperties(lines[0].properties));})) { result.skipped.push({rule:rule.key,reason:'marked_properties_differ'}); return; }
        var quantity = lines.reduce(function (sum, item) { return sum + (item.quantity || 0); }, 0);
        if (quantity < 2) return;
        if (requestMarker && cart.items.some(function (item) { return config.rules.some(function(targetRule){return toId(item.variant_id) === targetRule.to && linePlanId(item) === targetRule.planTo;}) && String(item.properties && item.properties._trial_gift_request || '') === requestMarker; })) {
          result.skipped.push({ rule:rule.key, reason:'marked_target_present', quantity:quantity }); return;
        }

        var targetPlan = 0;
        if (planId === 0) {
          if (!config.mergeOneTime) { result.skipped.push({ rule: rule.key, reason: 'one_time', quantity: quantity }); return; }
        } else {
          if (rule.planFrom && rule.planFrom !== planId) { result.skipped.push({ rule: rule.key, reason: 'plan_mismatch', quantity: quantity, plan: planId }); return; }
          if (!rule.planTo) { result.skipped.push({ rule: rule.key, reason: 'no_plan_to', quantity: quantity, plan: planId }); return; }
          targetPlan = rule.planTo;
        }

        var pairs = Math.floor(quantity / 2);
        var remainder = quantity % 2;
        var add = { type: 'add', id: rule.to, quantity: pairs };
        if (targetPlan) add.selling_plan = targetPlan;
        var properties = carriedProperties(lines[0].properties);
        if (properties) add.properties = properties;
        result.ops.push(add);
        lines.forEach(function (item, index) {
          var keep = index === 0 ? remainder : 0;
          if (keep !== item.quantity) result.ops.push({ type: 'change', key: item.key, quantity: keep });
        });
        result.merged.push({ rule: rule.key, from: rule.from, to: rule.to, pairs: pairs, remainder: remainder, plan: targetPlan, sourcePlan: planId, requestMarker: requestMarker, properties: properties });
      });
    });
    return result;
  }

  var api = { plan: plan, normaliseConfig: normaliseConfig, toId: toId, linePlanId: linePlanId, carriedProperties: carriedProperties, hiddenUpsellVariantIds: hiddenUpsellVariantIds };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;

  if (!window.AL_TRIAL_CART_MERGE) {
    try {
      var storedConfig = JSON.parse(window.localStorage.getItem('alTrialCartMergeConfig') || 'null');
      window.AL_TRIAL_CART_MERGE = storedConfig && storedConfig.version === 2 && storedConfig.expiresAt > Date.now()
        ? storedConfig.config || {}
        : {};
      if (!window.AL_TRIAL_CART_MERGE.enabled) window.localStorage.removeItem('alTrialCartMergeConfig');
    } catch (error) {
      window.AL_TRIAL_CART_MERGE = {};
    }
  }

  var initialConfig = normaliseConfig(window.AL_TRIAL_CART_MERGE);
  if (!initialConfig.enabled || !initialConfig.rules.length) return;

  var INTERNAL_HEADER = 'X-AL-Trial-Merge';
  var HALT_STORAGE_KEY = 'alTrialCartMergeHaltedV1';
  var LEASE_STORAGE_KEY = 'alTrialCartMergeLeaseV1';
  var runnerId = String(Date.now()) + '-' + Math.random().toString(36).slice(2);
  var running = false;
  var queued = false;
  var halted = false;
  var timer = null;
  var activeRun = null;
  var manualUpgradeLease = false;
  var leaseKind = null;
  var inspecting = null;
  var markedTransaction = false;
  var inspectedToken = null;
  var manualLeaseHeartbeat = null;

  try { halted = window.sessionStorage.getItem(HALT_STORAGE_KEY) === '1'; } catch (error) {}

  function setHalted(value) {
    halted = Boolean(value);
    try {
      if (halted) window.sessionStorage.setItem(HALT_STORAGE_KEY, '1');
      else window.sessionStorage.removeItem(HALT_STORAGE_KEY);
    } catch (error) {}
  }

  function acquireLease() {
    try {
      var now = Date.now();
      var current = JSON.parse(window.localStorage.getItem(LEASE_STORAGE_KEY) || 'null');
      if (current && current.owner !== runnerId && current.expiresAt > now) return false;
      var lease = { owner: runnerId, expiresAt: now + 30000 };
      window.localStorage.setItem(LEASE_STORAGE_KEY, JSON.stringify(lease));
      var saved = JSON.parse(window.localStorage.getItem(LEASE_STORAGE_KEY) || 'null');
      return Boolean(saved && saved.owner === runnerId);
    } catch (error) {
      return true;
    }
  }

  function releaseLease() {
    try {
      var current = JSON.parse(window.localStorage.getItem(LEASE_STORAGE_KEY) || 'null');
      if (current && current.owner === runnerId) window.localStorage.removeItem(LEASE_STORAGE_KEY);
    } catch (error) {}
  }

  function renewLease() {
    if (!manualUpgradeLease) return false;
    try {
      var current = JSON.parse(window.localStorage.getItem(LEASE_STORAGE_KEY) || 'null');
      if (!current || current.owner !== runnerId) return false;
      current.expiresAt = Date.now() + 30000;
      window.localStorage.setItem(LEASE_STORAGE_KEY, JSON.stringify(current));
      return true;
    } catch (error) {
      return true;
    }
  }

  function log() {
    var config = normaliseConfig(window.AL_TRIAL_CART_MERGE);
    if (config.debug && window.console) console.log.apply(console, [TAG].concat([].slice.call(arguments)));
  }

  function wait(ms) { return new Promise(function (resolve) { window.setTimeout(resolve, ms); }); }

  function cartRequest(path, body) {
    var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    headers[INTERNAL_HEADER] = '1';
    return fetch(path, { method: 'POST', credentials: 'same-origin', headers: headers, body: JSON.stringify(body) })
      .then(function (response) {
        if (!response.ok) { var error = new Error(path + ' ' + response.status); error.status = response.status; throw error; }
        return response.json();
      });
  }

  function readCart() {
    var headers = {};
    headers[INTERNAL_HEADER] = '1';
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: headers }).then(function (r) { if (!r.ok) { var error = new Error('Cart read failed: ' + r.status); error.status = r.status; throw error; } return r.json(); });
  }

  // Reduce the 8-day lines (variant + plan) down to `remainder`, always with fresh keys.
  function reduceSource(entry, attempt, record) {
    attempt = attempt || 0;
    return readCart().then(function (cart) {
      if (record) validateResume(cart, record);
      var lines = sourceLines(cart, entry.from, entry.sourcePlan, entry.requestMarker);
      var total = lines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
      if (total <= entry.remainder) return cart;
      if (attempt >= 4) throw new Error('8-day lines still present after retries');
      if (record) {
        var next = lines.map(function (item,index) { return {item:item, keep:index === 0 ? entry.remainder : 0}; }).filter(function (change) { return change.keep !== change.item.quantity; })[0];
        return cartRequest('/cart/change.js', {id:next.item.key,quantity:next.keep}).then(function () {
          return reduceSource(entry,attempt + 1,record);
        }, function (error) {
          if (error && error.status === 429) throw error;
          // An unknown absolute reduction is verified from a fresh, validated
          // snapshot before deciding whether another reduction is necessary.
          return wait(700).then(function () { return reduceSource(entry,attempt + 1,record); });
        });
      }
      var chain = Promise.resolve();
      lines.forEach(function (item, index) {
        var keep = index === 0 ? entry.remainder : 0;
        if (keep !== item.quantity) chain = chain.then(function () { return cartRequest('/cart/change.js', { id: item.key, quantity: keep }); });
      });
      return chain.then(function () { return reduceSource(entry, attempt + 1); }, function (error) {
        log('reduce retry', attempt + 1, String(error));
        return wait(700).then(function () { return reduceSource(entry, attempt + 1); });
      });
    });
  }

  // Best-effort undo of the 16-day add when the 8-day lines could not be reduced.
  function rollback(entry, added) {
    return readCart().then(function (cart) {
      var addedKey = added && added.key ? String(added.key) : '';
      var line = (cart.items || []).filter(function (item) {
        return addedKey && String(item.key) === addedKey;
      })[0];
      if (!line || line.quantity < entry.pairs) throw new Error('16-day rollback ownership could not be verified');
      return cartRequest('/cart/change.js', { id: line.key, quantity: Math.max(0, line.quantity - entry.pairs) });
    }).catch(function (error) { if (window.console) console.warn(TAG, 'rollback failed', error); });
  }

  // A cart-carried request marker identifies the owned transaction. A durable
  // journal prevents a new document from repeating an add interrupted by unload.
  var transactionBlocked = true;
  var transactionInspected = false;
  var transactionFailed = false;
  var journalPrefix = 'alTrialMarkedMergeV1:';
  function transactionState(blocked, failed) {
    transactionBlocked = Boolean(blocked);
    transactionInspected = true;
    transactionFailed = Boolean(failed);
    document.dispatchEvent(new CustomEvent('al:trial-transaction-state'));
  }
  function canonical(value) {
    if (!value || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
    return '{' + Object.keys(value).sort().map(function (key) { return JSON.stringify(key) + ':' + canonical(value[key]); }).join(',') + '}';
  }
  function journalKey(cart) {
    if (!cart || typeof cart.token !== 'string' || !cart.token) throw new Error('Trial cart token unavailable');
    return journalPrefix + encodeURIComponent(cart.token);
  }
  function loadJournal(cart) {
    var raw = window.localStorage.getItem(journalKey(cart));
    if (!raw) return null;
    var record = JSON.parse(raw);
    if (!record || record.version !== 1 || record.token !== cart.token || !record.entry || !record.entry.requestMarker) throw new Error('Trial merge journal invalid');
    return record;
  }
  function saveJournal(cart, record) {
    var raw = JSON.stringify(record), key = journalKey(cart);
    window.localStorage.setItem(key, raw);
    if (window.localStorage.getItem(key) !== raw) throw new Error('Trial merge journal persistence failed');
    transactionState(true);
  }
  function targetLines(cart, entry) {
    return cart.items.filter(function (line) { return toId(line.variant_id) === entry.to && linePlanId(line) === entry.plan && String(line.properties && line.properties._trial_gift_request || '') === entry.requestMarker && !line.properties._trial_gift_for; });
  }
  function unrelated(cart, entry) {
    return canonical(cart.items.filter(function (line) {
      if (String(line.properties && line.properties._trial_gift_request || '') !== entry.requestMarker) return true;
      return !(line.properties._trial_gift_for || toId(line.variant_id) === entry.from && linePlanId(line) === entry.sourcePlan || toId(line.variant_id) === entry.to && linePlanId(line) === entry.plan);
    }).map(function (line) { return { variant: line.variant_id, plan: linePlanId(line), properties: line.properties || {}, quantity: line.quantity, price: line.final_line_price }; }).sort(function (a,b) { return canonical(a).localeCompare(canonical(b)); }));
  }
  function ownedPrice(cart, entry) {
    return sourceLines(cart, entry.from, entry.sourcePlan, entry.requestMarker).concat(targetLines(cart, entry)).reduce(function (sum,line) { return sum + line.final_line_price; },0);
  }
  function sourceOwnership(lines) {
    var groups = {};
    lines.forEach(function (line) { var key=canonical(carriedProperties(line.properties)); groups[key]=(groups[key] || 0)+line.quantity; });
    return Object.keys(groups).sort().map(function (key) { return {properties:key,quantity:groups[key]}; });
  }
  function validateResume(cart, record) {
    var entry = record.entry, targets = targetLines(cart, entry), sources = sourceLines(cart, entry.from, entry.sourcePlan, entry.requestMarker);
    if (targets.length !== 1 || targets[0].quantity !== entry.pairs || canonical(carriedProperties(targets[0].properties)) !== canonical(entry.properties)) throw new Error('Trial target outcome remains unknown');
    var total = sources.reduce(function (sum,line) { return sum + line.quantity; },0);
    if (total < entry.remainder || total > record.sourceQuantity || unrelated(cart,entry) !== record.unrelated) throw new Error('Trial cart changed during interrupted merge');
    sourceOwnership(sources).forEach(function (line) {
      var savedQuantity = record.sources.filter(function (saved) { return saved.properties === line.properties; }).reduce(function(sum,saved){return sum+saved.quantity;},0);
      if (!savedQuantity || line.quantity > savedQuantity) throw new Error('Trial source ownership changed');
    });
    return total;
  }
  function reconcileJournal(cart, record) {
    validateResume(cart,record);
    return reduceSource(record.entry,0,record).then(function () { return readCart(); }).then(function (after) {
      var quantity = validateResume(after,record);
      if (quantity !== record.entry.remainder || ownedPrice(after,record.entry) !== record.price) throw new Error('Trial merge conservation unverified');
      var key = journalKey(after);
      window.localStorage.removeItem(key);
      if (window.localStorage.getItem(key) !== null) throw new Error('Trial merge journal clear failed');
      transactionState(false);
      return after;
    });
  }
  function markedEntry(entry) {
    return readCart().then(function (cart) {
      if (loadJournal(cart)) throw new Error('Trial merge already pending');
      if (cart.items.some(function(line){return String(line.properties && line.properties._trial_gift_request || '') === entry.requestMarker && initialConfig.rules.some(function(rule){return toId(line.variant_id)===rule.to && linePlanId(line)===rule.planTo;});})) throw new Error('Trial target already exists');
      var sources = sourceLines(cart,entry.from,entry.sourcePlan,entry.requestMarker);
      var quantity = sources.reduce(function(sum,line) { return sum+line.quantity; },0);
      if (quantity !== entry.pairs * 2 + entry.remainder) throw new Error('Trial source changed before add');
      if (sources.some(function(line){return canonical(carriedProperties(line.properties)) !== canonical(entry.properties);})) throw new Error('Trial source properties differ');
      var record = { version:1, token:cart.token, createdAt:Date.now(), entry:entry, sourceQuantity:quantity, sources:sourceOwnership(sources), unrelated:unrelated(cart,entry), price:ownedPrice(cart,entry), phase:'pending' };
      saveJournal(cart,record);
      // The journal is durable before the sole add. Unknown outcomes never roll
      // back or re-add; a later page may only reconcile a proven target delta.
      var body = {id:entry.to,quantity:entry.pairs,properties:entry.properties};
      if(entry.plan) body.selling_plan=entry.plan;
      return cartRequest('/cart/add.js',body).then(function(){return readCart();}).then(function(after){return reconcileJournal(after,record);});
    });
  }

  function executeEntry(entry) {
    if (entry.requestMarker) return markedEntry(entry);
    var body = { id: entry.to, quantity: entry.pairs };
    if (entry.plan) body.selling_plan = entry.plan;
    if (entry.properties) body.properties = entry.properties;
    return cartRequest('/cart/add.js', body).then(function (added) {
      return reduceSource(entry, 0).catch(function (error) {
        setHalted(true);
        if (window.console) console.warn(TAG, 'could not reduce 8-day lines; rolling back and halting', error);
        return rollback(entry, added).then(function () { throw error; });
      });
    });
  }

  function notify(result) {
    document.dispatchEvent(new CustomEvent('al:trial-cart-merged', { detail: result }));
    var drawer = document.querySelector('#g-cart-content');
    if (drawer && drawer.classList.contains('g-cart-open')) document.dispatchEvent(new Event('gOpenCart'));
  }

  function syncUpsellVisibility(cart, config) {
    var hiddenIds = hiddenUpsellVariantIds(cart, config).map(String);
    var slides = [].slice.call(document.querySelectorAll('#g-cart-content .g-upsells-wrap .swiper-slide[data-upsell-vid]'));
    slides.forEach(function (slide) {
      var ids = String(slide.getAttribute('data-upsell-variant-ids') || slide.getAttribute('data-upsell-vid') || '')
        .split(',').map(function (id) { return String(toId(id)); });
      var shouldHide = hiddenIds.some(function (id) { return ids.indexOf(id) !== -1; });
      slide.hidden = shouldHide;
      slide.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
    });
    [].slice.call(document.querySelectorAll('#g-cart-content .g-upsells-wrap')).forEach(function (wrap) {
      var hasVisible = [].slice.call(wrap.querySelectorAll('.swiper-slide[data-upsell-vid]')).some(function (slide) { return !slide.hidden; });
      wrap.hidden = !hasVisible;
    });
    var slider = document.querySelector('#g-cart-content .g-cart-upsells-slider');
    if (slider && slider.swiper && typeof slider.swiper.update === 'function') slider.swiper.update();
  }

  // Runs one merge pass. `override` lets QA pass an explicit config (never persisted).
  function run(override, ownsUpgrade) {
    var config = normaliseConfig(override || window.AL_TRIAL_CART_MERGE);
    // The explicit upgrade CTA owns the cart until it either verifies or
    // finishes rollback. Background folding must never mutate that transaction.
    if (manualUpgradeLease && !ownsUpgrade) {
      queued = true;
      return Promise.resolve({ ops: [], skipped: [], merged: [], manualUpgrade: true });
    }
    // One-time trial merging is only allowed inside the explicit upgrade CTA
    // lease. Quantity steppers and unrelated cart refreshes must never trigger it.
    if (ownsUpgrade && (!manualUpgradeLease || leaseKind !== 'upgrade')) return Promise.resolve({ ops: [], skipped: [], merged: [], noUpgradeLease: true });
    if (!ownsUpgrade) config.mergeOneTime = false;
    if (halted) {
      if (inspecting) return inspecting;
      inspecting = readCart().then(function(cart) {
      inspectedToken = cart.token;
      var marked=cart.items.some(function(line){return Boolean(line.properties && line.properties._trial_gift_request);});
      var journal;
      try { journal=loadJournal(cart); } catch(error) { if(marked)throw error; }
      transactionState(Boolean(journal),Boolean(journal));
      return {ops:[],skipped:[],merged:[],halted:true};
    }).catch(function(error){transactionState(true,true);return {ops:[],skipped:[],merged:[],halted:true,error:String(error)};}).then(function(result){inspecting=null;return result;});
      return inspecting;
    }
    if (!config.enabled || !config.rules.length) return Promise.resolve({ ops: [], skipped: [], merged: [], disabled: true });
    if (running) { queued = true; return activeRun || Promise.resolve({ ops: [], skipped: [], merged: [], busy: true }); }
    if (!acquireLease()) {
      schedule();
      return Promise.resolve({ ops: [], skipped: [], merged: [], busyOtherTab: true });
    }
    running = true;
    markedTransaction = false;
    function executeCart(cart, skipJournal) {
      var journal = skipJournal ? null : loadJournal(cart);
      if (journal) return reconcileJournal(cart,journal).then(function(){var result={ops:[],skipped:[],merged:[],recovered:true};notify(result);return result;});
      var result = plan(cart,config);
      syncUpsellVisibility(cart,config);
      var chain = Promise.resolve();
      result.merged.forEach(function(entry){chain=chain.then(function(){return executeEntry(entry);});});
      return chain.then(function(){transactionState(false);if(result.merged.length)notify(result);return result;});
    }
    activeRun = readCart().then(function(provisional) {
      inspectedToken = provisional.token;
      var marked = provisional.items.some(function(line){return Boolean(line.properties && line.properties._trial_gift_request);});
      var journal;
      markedTransaction = marked;
      try { journal=loadJournal(provisional); } catch(error) { if(marked)throw error; transactionState(false); return executeCart(provisional,true); }
      if (!marked && !journal) { transactionState(false); return executeCart(provisional,true); }
      markedTransaction = true;
      if (!navigator.locks || typeof navigator.locks.request !== 'function') throw new Error('Trial merge lock unavailable');
      return navigator.locks.request('al-trial-marked:'+provisional.token,{mode:'exclusive'},function(){return readCart().then(function(fresh){if(fresh.token!==provisional.token)throw new Error('Trial cart token changed');return executeCart(fresh);});});
    })
      .catch(function (error) { if (markedTransaction || !transactionInspected) transactionState(true,true); if (markedTransaction && error && error.status === 429) setHalted(true); if (window.console) console.warn(TAG, 'merge failed', error); return { ops: [], skipped: [], merged: [], error: String(error), halted: halted }; })
      .then(function (result) {
        running = false;
        if (halted) {
          queued = false;
          window.clearTimeout(timer);
          timer = null;
        } else if (queued) {
          queued = false;
          schedule();
        }
        activeRun = null;
        if (!manualUpgradeLease) releaseLease();
        return result;
      });
    return activeRun;
  }

  // Resolves only when all active and queued merge work is genuinely idle.
  // There is deliberately no timeout: checkout must remain blocked rather
  // than race a slow cart mutation and show a false rollback confirmation.
  function whenIdle() {
    if (inspecting) return inspecting.then(whenIdle);
    if (!running && !queued && !timer) return Promise.resolve();
    var current = activeRun || Promise.resolve();
    return Promise.resolve(current).then(function () { return wait(300); }).then(whenIdle);
  }

  function schedule() {
    if (manualUpgradeLease) {
      queued = true;
      return;
    }
    window.clearTimeout(timer);
    timer = window.setTimeout(function () { timer = null; run(); }, 250);
  }

  // Observe every cart mutation made by the theme, the cart drawer upsell or apps that use fetch.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var internal = false;
    try {
      var headers = init && init.headers;
      if (headers) internal = headers instanceof Headers ? headers.has(INTERNAL_HEADER) : Boolean(headers[INTERNAL_HEADER]);
    } catch (error) { internal = false; }
    if (!internal && (running && markedTransaction || transactionInspected && transactionBlocked) && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) return Promise.resolve(new Response(JSON.stringify({status:409,message:'Trial cart update in progress'}),{status:409,headers:{'Content-Type':'application/json'}}));
    var promise = nativeFetch.apply(this, arguments);
    if (!internal && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url)) promise.then(function () { schedule(); }, function () {});
    return promise;
  };

  document.addEventListener('gOpenCart', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();

  function beginManualLease(kind) {
    if (!transactionInspected || transactionBlocked || transactionFailed || running || activeRun || inspecting || manualUpgradeLease || kind === 'upgrade' && halted) return false;
    if (kind === 'gift') { try { if (!inspectedToken || loadJournal({token:inspectedToken})) return false; } catch(error) { return false; } }
    if (!acquireLease()) return false;
    manualUpgradeLease = true;
    leaseKind = kind;
    window.clearTimeout(timer);
    timer = null;
    window.clearInterval(manualLeaseHeartbeat);
    manualLeaseHeartbeat = window.setInterval(renewLease,10000);
    return true;
  }
  function endManualLease(kind) {
    if (!manualUpgradeLease || leaseKind !== kind) return;
    manualUpgradeLease = false;
    leaseKind = null;
    window.clearInterval(manualLeaseHeartbeat);
    manualLeaseHeartbeat = null;
    releaseLease();
    if (queued) { queued=false; schedule(); }
  }
  window.alTrialCartMerge = {
    run: run, runUpgrade: function ownerRun() {
      if (!manualUpgradeLease || leaseKind !== 'upgrade') return Promise.resolve({ ops: [], skipped: [], merged: [], noUpgradeLease: true });
      if (running) return Promise.resolve(activeRun).then(ownerRun);
      return run(undefined, true);
    }, plan: plan, normaliseConfig: normaliseConfig, hiddenUpsellVariantIds: hiddenUpsellVariantIds,
    reset: function () {
      if (running || activeRun) return false;
      setHalted(false);
      leaseKind = null;
      manualUpgradeLease = false;
      queued = false;
      window.clearTimeout(timer);
      timer = null;
      window.clearInterval(manualLeaseHeartbeat);
      manualLeaseHeartbeat = null;
      releaseLease();
      return true;
    },
    whenIdle: whenIdle,
    beginUpgrade: function () { return beginManualLease('upgrade'); },
    endUpgrade: function () { endManualLease('upgrade'); },
    beginGiftSync: function () { return beginManualLease('gift'); },
    giftSyncAllowed: function (cart) {
      if (leaseKind !== 'gift' || !manualUpgradeLease || transactionBlocked || !cart || cart.token !== inspectedToken) return false;
      try { return !loadJournal(cart); } catch(error) { return false; }
    },
    endGiftSync: function () { endManualLease('gift'); },
    get transactionBlocked() { return transactionBlocked; },
    get transactionInspected() { return transactionInspected; },
    get transactionFailed() { return transactionFailed; },
    get running() { return running; },
    get halted() { return halted; }
  };
})();
